export type PropertyStatus =
  | 'new'
  | 'interested'
  | 'viewing'
  | 'applied'
  | 'rejected'
  | 'excluded'
  | 'rented';

export type Site =
  // UK - Active
  | 'rightmove'
  | 'zoopla'
  | 'openrent'
  | 'onthemarket'
  | 'dexters'
  | 'foxtons'
  | 'winkworth'
  | 'belvoir'
  | 'benhams'
  // UK - Legacy (kept for backward compatibility)
  | 'squiresestates'
  | 'ashmoreestates'
  | 'theroundtree'
  | 'shproperties'
  | 'martyngerrard'
  | 'krispyhouse'
  | 'bramptonrealestate'
  // USA
  | 'zillow'
  | 'realtor'
  | 'redfin'
  | 'apartments'
  | 'trulia'
  // France
  | 'seloger'
  | 'leboncoin'
  | 'pap'
  | 'logicimmo'
  | 'bienici'
  // Germany
  | 'immobilienscout24'
  | 'immonet'
  | 'immowelt'
  | 'wggesucht'
  | 'kleinanzeigen'
  // Saudi Arabia
  | 'aqar'
  | 'bayut_sa'
  | 'wasalt'
  | 'haraj'
  | 'ejar'
  // UAE
  | 'bayut'
  | 'propertyfinder'
  | 'dubizzle'
  | 'houza'
  | 'justproperty'
  // Manual entry
  | 'manual';

export const SITE_DISPLAY_NAMES: Record<Site, string> = {
  // UK
  rightmove: 'Rightmove',
  zoopla: 'Zoopla',
  openrent: 'OpenRent',
  onthemarket: 'OnTheMarket',
  dexters: 'Dexters',
  foxtons: 'Foxtons',
  winkworth: 'Winkworth',
  belvoir: 'Belvoir',
  benhams: 'Benhams',
  // UK Legacy
  squiresestates: 'Squires Estates',
  ashmoreestates: 'Ashmore Estates',
  theroundtree: 'The Roundtree',
  shproperties: 'SH Properties',
  martyngerrard: 'Martyn Gerrard',
  krispyhouse: 'Krispy House',
  bramptonrealestate: 'Brampton Real Estate',
  // USA
  zillow: 'Zillow',
  realtor: 'Realtor.com',
  redfin: 'Redfin',
  apartments: 'Apartments.com',
  trulia: 'Trulia',
  // France
  seloger: 'SeLoger',
  leboncoin: 'LeBonCoin',
  pap: 'PAP',
  logicimmo: 'Logic-Immo',
  bienici: 'BienIci',
  // Germany
  immobilienscout24: 'ImmobilienScout24',
  immonet: 'Immonet',
  immowelt: 'Immowelt',
  wggesucht: 'WG-Gesucht',
  kleinanzeigen: 'Kleinanzeigen',
  // Saudi Arabia
  aqar: 'Aqar',
  bayut_sa: 'Bayut.sa',
  wasalt: 'Wasalt',
  haraj: 'Haraj',
  ejar: 'Ejar',
  // UAE
  bayut: 'Bayut',
  propertyfinder: 'PropertyFinder',
  dubizzle: 'Dubizzle',
  houza: 'Houza',
  justproperty: 'JustProperty',
  // Manual
  manual: 'Manual',
};

export type FurnishedType = 'furnished' | 'unfurnished' | 'part-furnished';

export interface TravelTime {
  locationName: string;
  walkingMinutes: number;
  transitMinutes: number;
  distanceKm: number;
}

export interface AdditionalCost {
  name: string;
  amount: number;
}

export interface Comment {
  id: string;
  author: string;
  text: string;
  images?: string[];
  createdAt: string;
  editedAt?: string;
}

export interface Photo {
  id: string;
  url: string;
  caption?: string;
  addedBy: string;
  addedAt: string;
}

export interface Property {
  id: string;
  url: string;
  address: string;
  site: Site;
  listingType?: 'rent' | 'sale';
  rent: number;
  deposit?: number;
  billsIncluded: boolean;
  furnished: FurnishedType;
  availableFrom?: string;
  minTenancy?: string;
  bedrooms: number;
  bathrooms: number;
  propertyType: string;
  floorArea?: number;
  floor?: string;
  hasGarden: boolean;
  hasParking: boolean;
  petFriendly?: boolean;
  epcRating?: string;
  councilTaxBand?: string;
  features: string[];
  images: string[];
  photos: Photo[];
  travelTimes: TravelTime[];
  status: PropertyStatus;
  viewingDate?: string;
  notes?: string;
  comments: Comment[];
  userRating?: number;
  otherRating?: number;
  excludeReason?: string;
  contactName?: string;
  contactPhone?: string;
  councilTaxYearly?: number;
  utilitiesMonthly?: number;
  additionalCosts: AdditionalCost[];
  scrapedAt: string;
  createdAt: string;
  updatedAt: string;
  locked?: boolean;
  isDemo?: boolean;
}

export interface PropertySummary {
  id: string;
  url: string;
  address: string;
  site: Site;
  listingType?: 'rent' | 'sale';
  rent: number;
  status: PropertyStatus;
  bedrooms: number;
  bathrooms: number;
  floorArea?: number;
  furnished: FurnishedType;
  viewingDate?: string;
  userRating?: number;
  images: string[];
  travelTimes: TravelTime[];
  locked?: boolean;
  isDemo?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListPropertiesTierInfo {
  tier: string;
  isReadOnly: boolean;
  trialEndsAt: string | null;
}

export interface ListPropertiesResponse {
  properties: PropertySummary[];
  tierInfo?: ListPropertiesTierInfo;
}

export interface CreatePropertyRequest {
  searchId: string;
  address: string;
  rent: number;
  listingType: 'rent' | 'sale';
  site: 'manual';
  url?: string;
}

export interface CreatePropertyResponse {
  id: string;
  url: string;
  address: string;
  status: PropertyStatus;
  createdAt: string;
}

export interface UpdatePropertyRequest {
  address?: string;
  rent?: number;
  bedrooms?: number;
  bathrooms?: number;
  propertyType?: string;
  furnished?: FurnishedType;
  status?: PropertyStatus;
  notes?: string;
  addComment?: {
    author: string;
    text: string;
  };
  removeCommentId?: string;
  editComment?: {
    commentId: string;
    text: string;
  };
  addPhoto?: {
    url: string;
    addedBy: string;
    caption?: string;
  };
  removePhotoId?: string;
  userRating?: number;
  otherRating?: number;
  excludeReason?: string;
  // Editable feature flags
  hasGarden?: boolean;
  hasParking?: boolean;
  petFriendly?: boolean;
  billsIncluded?: boolean;
  features?: string[];
  councilTaxYearly?: number | null;
  utilitiesMonthly?: number | null;
  additionalCosts?: AdditionalCost[];
  viewingDate?: string | null;
  contactName?: string;
  contactPhone?: string;
}

export interface UpdatePropertyResponse {
  id: string;
  address: string;
  rent: number;
  status: PropertyStatus;
  notes?: string;
  comments: Comment[];
  photos: Photo[];
  userRating?: number;
  otherRating?: number;
  excludeReason?: string;
  bedrooms: number;
  bathrooms: number;
  propertyType: string;
  furnished: FurnishedType;
  hasGarden: boolean;
  hasParking: boolean;
  petFriendly?: boolean;
  billsIncluded: boolean;
  features: string[];
  viewingDate: string | null;
  contactName: string | null;
  contactPhone: string | null;
  councilTaxYearly: number | null;
  utilitiesMonthly: number | null;
  additionalCosts: AdditionalCost[];
  updatedAt: string;
}

export interface Location {
  name: string;
  address: string;
  icon?: string;
  color?: string;
  isPrimary?: boolean;
  order: number;
}

export interface TravelTimeDestination {
  name: string;
  address: string;
}

export interface TravelTimeResult {
  locationName: string;
  locationAddress: string;
  walkingMinutes: number | null;
  drivingMinutes: number | null;
  transitMinutes: number | null;
  distanceKm: number | null;
  cached: boolean;
  error?: string;
}

export interface CalculateTravelTimesResponse {
  travelTimes: TravelTimeResult[];
}

export interface SiteRequestResponse {
  domain: string;
  requestCount: number;
}

export type SearchStatus = 'active' | 'completed' | 'archived';

export interface SearchMember {
  userId: string;
  name: string;
  joinedAt: string;
  invitedBy?: string;
}

export interface GeocodedArea {
  label: string;
  placeId: string;
  lat: number;
  lng: number;
  radiusKm?: number;
}

export interface SearchCriteria {
  budgetMin?: number;
  budgetMax?: number;
  budgetTarget?: number;
  targetAreas: string[];
  searchAreas?: GeocodedArea[];
  minBedrooms?: number;
  moveInFrom?: string;
  moveInTo?: string;
  type?: 'rent' | 'buy';
  sharedMustHaves?: string[];
  sharedDealbreakers?: string[];
  sharedNiceToHaves?: string[];
}

export interface CommuteLocation {
  label: string;
  address: string;
  placeId: string;
  lat: number;
  lng: number;
  frequency: 'daily' | '3x_week' | '2x_week' | 'weekly' | 'occasional';
  maxCommuteMinutes: number;
  travelMode?: 'transit' | 'cycling' | 'walking' | 'driving';
}

export interface MemberPreferences {
  searchId: string;
  userId: string;
  commuteLocations: CommuteLocation[];
  personalMustHaves: string[];
  personalDealbreakers: string[];
  personalNiceToHaves: string[];
  onboardingCompletedAt?: string;
  emailNotificationsEnabled?: boolean;
  updatedAt: string;
}

export interface FlatSearch {
  id: string;
  name: string;
  status: SearchStatus;
  createdBy: string;
  criteria: SearchCriteria;
  members: SearchMember[];
  inviteCode?: string;
  rentedPropertyId?: string;
  startDate: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StructuredAction {
  type: string;
  field: string;
  value: unknown;
  target: 'shared' | 'personal';
}

export interface ChatMessage {
  id: string;
  searchId: string;
  userId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  structuredAction?: StructuredAction;
  createdAt: string;
}

export interface ChatResponse {
  message: string;
  actions: StructuredAction[];
  messageId: string;
}

export interface ChatHistoryResponse {
  messages: ChatMessage[];
  nextCursor?: string;
}
